import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import CustomJobFieldsManager from "@/components/CustomJobFieldsManager";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Trash2, Plus, GripVertical, X, CreditCard, ArrowRight, Mail, Check as CheckIcon } from "lucide-react";

function DeductionCategoriesEditor(props) {
  return <CategoriesEditor {...props} placeholder="Add a category (e.g., Fuel, Sub-contractor)" testPrefix="category" />;
}

function ShopTimeCategoriesEditor(props) {
  return <CategoriesEditor {...props} placeholder="Add a category (e.g., Shop work, Training)" testPrefix="shop-cat" />;
}

function InspectionItemsEditor(props) {
  return <CategoriesEditor {...props} placeholder="Add an inspection item (e.g., Check Oil Level)" testPrefix="inspection-item" />;
}

function CategoriesEditor({ value, onChange, placeholder, testPrefix }) {
  const [newCat, setNewCat] = React.useState("");
  const add = () => {
    const v = newCat.trim();
    if (!v) return;
    if ((value || []).map((c) => c.toLowerCase()).includes(v.toLowerCase())) {
      toast.error("Category already exists");
      return;
    }
    onChange([...(value || []), v]);
    setNewCat("");
  };
  const remove = (idx) => onChange((value || []).filter((_, i) => i !== idx));
  const rename = (idx, v) => onChange((value || []).map((c, i) => (i === idx ? v : c)));
  return (
    <div className="space-y-2">
      {(value || []).length === 0 && (
        <p className="text-xs text-slate-500">No categories yet. Add at least one.</p>
      )}
      {(value || []).map((cat, idx) => (
        <div key={idx} className="flex items-center gap-2" data-testid={`${testPrefix}-row-${idx}`}>
          <GripVertical className="h-4 w-4 text-slate-400 flex-shrink-0" />
          <Input
            value={cat}
            onChange={(e) => rename(idx, e.target.value)}
            className="flex-1"
            data-testid={`${testPrefix}-input-${idx}`}
          />
          <Button type="button" variant="ghost" size="sm" onClick={() => remove(idx)} className="text-red-600" data-testid={`${testPrefix}-remove-${idx}-btn`}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
        <Input
          placeholder={placeholder}
          value={newCat}
          onChange={(e) => setNewCat(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          data-testid={`${testPrefix}-new-input`}
        />
        <Button type="button" onClick={add} className="bg-sky-600 hover:bg-sky-700" data-testid={`${testPrefix}-add-btn`}>
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
}

const DAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];

const US_STATES = [
  ["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],["CA","California"],
  ["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],["DC","District of Columbia"],
  ["FL","Florida"],["GA","Georgia"],["HI","Hawaii"],["ID","Idaho"],["IL","Illinois"],
  ["IN","Indiana"],["IA","Iowa"],["KS","Kansas"],["KY","Kentucky"],["LA","Louisiana"],
  ["ME","Maine"],["MD","Maryland"],["MA","Massachusetts"],["MI","Michigan"],["MN","Minnesota"],
  ["MS","Mississippi"],["MO","Missouri"],["MT","Montana"],["NE","Nebraska"],["NV","Nevada"],
  ["NH","New Hampshire"],["NJ","New Jersey"],["NM","New Mexico"],["NY","New York"],
  ["NC","North Carolina"],["ND","North Dakota"],["OH","Ohio"],["OK","Oklahoma"],["OR","Oregon"],
  ["PA","Pennsylvania"],["RI","Rhode Island"],["SC","South Carolina"],["SD","South Dakota"],
  ["TN","Tennessee"],["TX","Texas"],["UT","Utah"],["VT","Vermont"],["VA","Virginia"],
  ["WA","Washington"],["WV","West Virginia"],["WI","Wisconsin"],["WY","Wyoming"],
  ["PR","Puerto Rico"],["VI","U.S. Virgin Islands"],["GU","Guam"],["AS","American Samoa"],["MP","Northern Mariana Islands"],
];

export default function AdminSettings() {
  const { user, refresh: refreshUser } = useAuth();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const branding = useBranding();

  // Change-my-login-email flow
  const [emailForm, setEmailForm] = useState({ new_email: "", current_password: "" });
  const [emailBusy, setEmailBusy] = useState(false);

  const saveMyEmail = async (e) => {
    e?.preventDefault?.();
    const newEmail = emailForm.new_email.trim().toLowerCase();
    if (!newEmail || !emailForm.current_password) {
      toast.error("Enter both the new email and your current password");
      return;
    }
    if (newEmail === (user?.email || "").toLowerCase()) {
      toast.info("That's already your current email");
      return;
    }
    setEmailBusy(true);
    try {
      await api.post("/auth/change-email", {
        new_email: newEmail,
        current_password: emailForm.current_password,
      });
      toast.success(`Login email updated to ${newEmail}. Log out and back in to refresh your session.`);
      setEmailForm({ new_email: "", current_password: "" });
      await refreshUser();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setEmailBusy(false);
    }
  };

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data));
  }, []);

  const uploadLogo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Logo must be an image"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, company_logo_path: data.company_logo_path }));
      await branding.refresh();
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setUploading(false); }
  };

  const removeLogo = async () => {
    if (!confirm("Remove company logo?")) return;
    try {
      await api.delete("/settings/logo");
      setForm((f) => ({ ...f, company_logo_path: "" }));
      await branding.refresh();
      toast.success("Logo removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (!form) return <div className="py-20 text-center text-slate-500">Loading…</div>;

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        overtime_threshold: Number(form.overtime_threshold),
        default_overtime_multiplier: Number(form.default_overtime_multiplier),
        default_commission_percentage: Number(form.default_commission_percentage),
        work_week_start_day: Number(form.work_week_start_day),
      };
      const { data } = await api.put("/settings", payload);
      setForm(data);
      await branding.refresh();
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-settings-page">
      <header>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Admin</p>
        <h1 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900 mt-1">Payroll Settings</h1>
      </header>

      {/* Your login email — self-service, no dev intervention. Any admin can
          change their own login email in ~10 seconds without touching Team
          Members. Kept up here (above payroll fields) because "I can't log in"
          panic-emails from admins are the #1 support ticket driver. */}
      <Card className="rounded-2xl border-slate-200" data-testid="my-login-email-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-1">
            <div className="h-9 w-9 rounded-lg bg-sky-100 flex items-center justify-center shrink-0">
              <Mail className="h-4 w-4 text-sky-700" />
            </div>
            <div>
              <p className="font-extrabold font-display text-slate-900 leading-tight">Your login email</p>
              <p className="text-xs text-slate-500 leading-tight mt-0.5">Change the email you sign in with. Requires your current password.</p>
            </div>
          </div>
          <div className="ml-12 mt-3 space-y-3">
            <div className="text-xs text-slate-600 flex items-center gap-1.5">
              Currently signed in as: <span className="font-mono font-semibold text-slate-900" data-testid="current-login-email">{user?.email || "—"}</span>
            </div>
            <form onSubmit={saveMyEmail} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
              <div>
                <Label className="text-xs">New email</Label>
                <Input
                  type="email"
                  autoComplete="off"
                  placeholder="you@example.com"
                  value={emailForm.new_email}
                  onChange={(e) => setEmailForm((f) => ({ ...f, new_email: e.target.value }))}
                  data-testid="new-email-input"
                />
              </div>
              <div>
                <Label className="text-xs">Current password</Label>
                <Input
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={emailForm.current_password}
                  onChange={(e) => setEmailForm((f) => ({ ...f, current_password: e.target.value }))}
                  data-testid="current-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={emailBusy || !emailForm.new_email || !emailForm.current_password}
                className="bg-sky-600 hover:bg-sky-700 h-10"
                data-testid="change-email-btn"
              >
                {emailBusy ? "Saving…" : (<><CheckIcon className="h-4 w-4 mr-1" /> Update</>)}
              </Button>
            </form>
            <p className="text-[11px] text-slate-500">
              After the change, you may need to <b>log out and log back in</b> for menus to reflect the new address.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-5 space-y-4">
          <div>
            <h3 className="font-bold font-display text-base">Company Logo</h3>
            <p className="text-xs text-slate-500">Shown on the login screen, sidebar, paystubs, and reports. PNG/JPG/SVG, up to 5 MB.</p>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div className="h-24 w-24 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
              {branding.logoUrl ? (
                <img src={branding.logoUrl} alt="Company logo" className="h-20 w-20 object-contain" data-testid="settings-logo-preview" />
              ) : (
                <span className="text-xs text-slate-400">No logo</span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label className="inline-flex">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="settings-logo-input"
                  onChange={(e) => uploadLogo(e.target.files?.[0])}
                />
                <Button asChild disabled={uploading} className="bg-sky-600 hover:bg-sky-700">
                  <span className="cursor-pointer">
                    <Upload className="h-4 w-4 mr-1" /> {uploading ? "Uploading…" : (branding.logoUrl ? "Replace logo" : "Upload logo")}
                  </span>
                </Button>
              </label>
              {branding.logoUrl && (
                <Button variant="ghost" className="text-red-600 hover:text-red-700 justify-start" onClick={removeLogo} data-testid="settings-logo-remove-btn">
                  <Trash2 className="h-4 w-4 mr-1" /> Remove logo
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-5 space-y-3">
          <div>
            <h3 className="font-bold font-display text-base">Allowable Deduction Categories</h3>
            <p className="text-xs text-slate-500">Commission-based employees enter dollar amounts under these categories on each job. Add, rename, or remove as needed.</p>
          </div>
          <DeductionCategoriesEditor
            value={form.allowable_deduction_categories || []}
            onChange={(arr) => setForm({ ...form, allowable_deduction_categories: arr })}
          />
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-slate-200" data-testid="shop-time-categories-card">
        <CardContent className="p-5 space-y-3">
          <div>
            <h3 className="font-bold font-display text-base">Shop / Training Time Categories</h3>
            <p className="text-xs text-slate-500">Categories employees can pick when logging non-job hours (e.g., Shop work, Training, Travel). These hours are paid at the employee&apos;s hourly rate after admin approval.</p>
          </div>
          <ShopTimeCategoriesEditor
            value={form.shop_time_categories || []}
            onChange={(arr) => setForm({ ...form, shop_time_categories: arr })}
          />
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-slate-200" data-testid="truck-inspection-card">
        <CardContent className="p-5 space-y-4">
          <div>
            <h3 className="font-bold font-display text-base">Truck Pre-Inspection</h3>
            <p className="text-xs text-slate-500">Required for lead technicians/drivers after clock-in. On required days, employees cannot finalize until completed.</p>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Required Days</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {DAYS.map((d, idx) => {
                const selected = (form.truck_inspection_required_days || []).includes(idx);
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      const cur = form.truck_inspection_required_days || [];
                      const next = selected ? cur.filter((x) => x !== idx) : [...cur, idx].sort((a, b) => a - b);
                      setForm({ ...form, truck_inspection_required_days: next });
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border transition-colors ${
                      selected
                        ? "bg-sky-600 text-white border-sky-600"
                        : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                    }`}
                    data-testid={`required-day-${idx}-btn`}
                  >
                    {d.slice(0, 3)}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Tap to toggle. If no days are selected, the inspection is optional.</p>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Checklist Items</Label>
            <div className="mt-2">
              <InspectionItemsEditor
                value={form.truck_inspection_items || []}
                onChange={(arr) => setForm({ ...form, truck_inspection_items: arr })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-5 grid md:grid-cols-2 gap-3">
          <Field label="Company Name"><Input value={form.company_name || ""} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="settings-company-name" /></Field>
          <Field label="Company Phone"><Input value={form.company_phone || ""} onChange={(e) => setForm({ ...form, company_phone: e.target.value })} data-testid="settings-company-phone" /></Field>
          <Field label="Street Address" className="md:col-span-2">
            <Input
              value={form.company_street || ""}
              onChange={(e) => setForm({ ...form, company_street: e.target.value })}
              placeholder="123 Main Street"
              data-testid="settings-company-street"
            />
          </Field>
          <Field label="Suite / Unit (optional)">
            <Input
              value={form.company_suite || ""}
              onChange={(e) => setForm({ ...form, company_suite: e.target.value })}
              placeholder="Ste 200"
              data-testid="settings-company-suite"
            />
          </Field>
          <Field label="City">
            <Input
              value={form.company_city || ""}
              onChange={(e) => setForm({ ...form, company_city: e.target.value })}
              placeholder="Lexington"
              data-testid="settings-company-city"
            />
          </Field>
          <Field label="State">
            <Select value={form.company_state || ""} onValueChange={(v) => setForm({ ...form, company_state: v })}>
              <SelectTrigger data-testid="settings-company-state"><SelectValue placeholder="Select state" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {US_STATES.map(([code, name]) => (
                  <SelectItem key={code} value={code}>{code} — {name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="ZIP Code">
            <Input
              value={form.company_zip || ""}
              onChange={(e) => setForm({ ...form, company_zip: e.target.value.replace(/[^\d-]/g, "").slice(0, 10) })}
              placeholder="40502"
              inputMode="numeric"
              data-testid="settings-company-zip"
            />
          </Field>
          <Field label="Work Week Start">
            <Select value={String(form.work_week_start_day ?? 0)} onValueChange={(v) => setForm({ ...form, work_week_start_day: Number(v) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {DAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Pay Period Frequency">
            <Select value={form.pay_period_frequency || "weekly"} onValueChange={(v) => setForm({ ...form, pay_period_frequency: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="biweekly">Bi-Weekly</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Overtime Threshold (hours)"><Input type="number" step="0.5" value={form.overtime_threshold || 40} onChange={(e) => setForm({ ...form, overtime_threshold: e.target.value })} data-testid="settings-ot-threshold" /></Field>
          <Field label="Default OT Multiplier"><Input type="number" step="0.1" value={form.default_overtime_multiplier || 1.5} onChange={(e) => setForm({ ...form, default_overtime_multiplier: e.target.value })} /></Field>
          <Field label="Default Commission %"><Input type="number" step="0.1" value={form.default_commission_percentage || 0} onChange={(e) => setForm({ ...form, default_commission_percentage: e.target.value })} /></Field>
          <Field label="Taxes Enabled Globally">
            <div className="flex items-center gap-2 h-10">
              <Switch checked={!!form.taxes_enabled_globally} onCheckedChange={(v) => setForm({ ...form, taxes_enabled_globally: v })} />
              <span className="text-sm">{form.taxes_enabled_globally ? "Enabled — configure rates on the Taxes page" : "Disabled"}</span>
            </div>
          </Field>
          <Field label="GPS Required for Punches">
            <div className="flex items-center gap-2 h-10">
              <Switch
                checked={form.gps_required !== false}
                onCheckedChange={(v) => setForm({ ...form, gps_required: v })}
                data-testid="settings-gps-required-switch"
              />
              <span className="text-sm">{form.gps_required !== false ? "Required (recommended)" : "Optional — punches allowed without GPS"}</span>
            </div>
          </Field>
          <Field label="Job Photo Attachments">
            <div className="flex items-center gap-2 h-10">
              <Switch
                checked={!!form.job_photos_enabled}
                onCheckedChange={(v) => setForm({ ...form, job_photos_enabled: v })}
                data-testid="settings-job-photos-switch"
              />
              <span className="text-sm">
                {form.job_photos_enabled
                  ? "On — employees can attach photos (camera or gallery) to job entries"
                  : "Off — photo upload is hidden from the employee job entry form"}
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Photos are stored in encrypted object storage and visible to admins from the Daily Submission detail page.</p>
          </Field>
          <Field label="Benefits &amp; PTO Tracking">
            <div className="flex items-center gap-2 h-10">
              <Switch
                checked={!!form.benefits_enabled}
                onCheckedChange={(v) => setForm({ ...form, benefits_enabled: v })}
                data-testid="settings-benefits-switch"
              />
              <span className="text-sm">
                {form.benefits_enabled
                  ? "On — configure each employee's benefits from Admin → Employees. Balances also visible to enabled employees."
                  : "Off — benefits UI stays hidden on both admin and employee side."}
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Vacation, Sick, Personal, Holidays, Bereavement, Jury — each enabled per employee.</p>
          </Field>
        </CardContent>
      </Card>

      <CustomJobFieldsManager />

      <BillingQuickLink />

      <EmailReminderSettings form={form} setForm={setForm} />

      <Button onClick={save} disabled={busy} className="bg-sky-600 hover:bg-sky-700 h-12 px-8" data-testid="save-settings-btn">Save Settings</Button>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}


const WEEKDAY_LABELS = [
  { v: 0, s: "Mon" }, { v: 1, s: "Tue" }, { v: 2, s: "Wed" },
  { v: 3, s: "Thu" }, { v: 4, s: "Fri" }, { v: 5, s: "Sat" }, { v: 6, s: "Sun" },
];

function EmailReminderSettings({ form, setForm }) {
  const [status, setStatus] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);
  const [runningNow, setRunningNow] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/email-reminders/status");
        setStatus(data);
        setTestTo(data.test_recipient_default || "");
      } catch {
        setStatus({ resend_configured: false });
      }
    })();
  }, []);

  const hour = form.email_reminder_hour ?? 20;
  const minute = form.email_reminder_minute ?? 0;
  const weekdays = form.email_reminder_weekdays ?? [0, 1, 2, 3, 4, 5, 6];
  const enabled = !!form.email_reminder_enabled;

  const toggleDay = (d) => {
    const next = weekdays.includes(d) ? weekdays.filter((x) => x !== d) : [...weekdays, d].sort();
    setForm({ ...form, email_reminder_weekdays: next });
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/email-reminders/test", { to: testTo || undefined });
      toast.success(`Test email sent to ${data.to}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  const runNow = async () => {
    if (!confirm("Send reminder emails NOW to every employee in your company who clocked out today but hasn't finalized? Each user gets at most one email per day.")) return;
    setRunningNow(true);
    try {
      const { data } = await api.post("/admin/email-reminders/run-now");
      toast.success(data.sent > 0 ? `Sent ${data.sent} reminder${data.sent === 1 ? "" : "s"}` : "No employees needed a reminder right now");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setRunningNow(false);
    }
  };

  return (
    <Card className="rounded-2xl border-slate-200" data-testid="email-reminder-card">
      <CardContent className="p-5 space-y-4">
        <div>
          <h3 className="font-bold font-display text-base">Daily Finalize-Reminder Emails</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Automatically email every employee who clocked out today but hasn&apos;t tapped <span className="font-semibold">Finalize Day</span>. Each user only gets one email per day.
          </p>
        </div>

        {!status?.resend_configured && (
          <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-800">
            Resend API key is not configured on the server. Contact your developer to add <code className="px-1 bg-red-100 rounded">RESEND_API_KEY</code> to the backend env.
          </div>
        )}

        <Field label="Enabled">
          <div className="flex items-center gap-2 h-10">
            <Switch
              checked={enabled}
              onCheckedChange={(v) => setForm({ ...form, email_reminder_enabled: v })}
              disabled={!status?.resend_configured}
              data-testid="email-reminder-enabled-switch"
            />
            <span className="text-sm">
              {enabled ? "On — reminders will go out at the time below" : "Off — no reminder emails will be sent"}
            </span>
          </div>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Send Time (Hour, company TZ)">
            <Select value={String(hour)} onValueChange={(v) => setForm({ ...form, email_reminder_hour: Number(v) })}>
              <SelectTrigger data-testid="email-reminder-hour-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {Array.from({ length: 24 }).map((_, h) => (
                  <SelectItem key={h} value={String(h)}>
                    {h === 0 ? "12 AM" : h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h - 12} PM`} ({String(h).padStart(2, "0")}:00)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Minute">
            <Select value={String(minute)} onValueChange={(v) => setForm({ ...form, email_reminder_minute: Number(v) })}>
              <SelectTrigger data-testid="email-reminder-minute-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[0, 15, 30, 45].map((m) => <SelectItem key={m} value={String(m)}>:{String(m).padStart(2, "0")}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <Field label="Send on these days">
          <div className="flex flex-wrap gap-2 mt-1">
            {WEEKDAY_LABELS.map((d) => {
              const on = weekdays.includes(d.v);
              return (
                <button
                  type="button"
                  key={d.v}
                  onClick={() => toggleDay(d.v)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition border ${
                    on
                      ? "bg-sky-600 border-sky-600 text-white"
                      : "bg-white border-slate-300 text-slate-600 hover:border-slate-400"
                  }`}
                  data-testid={`email-reminder-weekday-${d.v}`}
                >
                  {d.s}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Active timezone: <span className="font-semibold">{status?.timezone || form.timezone || "America/New_York"}</span>
          </p>
        </Field>

        <div className="border-t border-slate-100 pt-3 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-700">Test &amp; Manual Send</p>
          <div className="flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs text-slate-600">Test recipient</Label>
              <Input
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="you@example.com"
                data-testid="email-reminder-test-to"
                className="mt-1"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={sendTest}
              disabled={sending || !status?.resend_configured}
              data-testid="email-reminder-test-btn"
            >
              {sending ? "Sending…" : "Send test email"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={runNow}
              disabled={runningNow || !status?.resend_configured}
              className="border-amber-300 text-amber-800 hover:bg-amber-50"
              data-testid="email-reminder-run-now-btn"
            >
              {runningNow ? "Sending…" : "Send reminders now"}
            </Button>
          </div>
          <p className="text-xs text-slate-500" data-testid="email-sender-note">
            {(() => {
              const sender = status?.sender_email || "";
              const isSandbox = !sender || sender.endsWith("@resend.dev");
              if (isSandbox) {
                return (
                  <>
                    With the free <code className="px-1 bg-slate-100 rounded">{sender || "onboarding@resend.dev"}</code> sender, emails will only deliver to the address you signed up for Resend with. To send to all employees, verify your own domain in Resend.
                  </>
                );
              }
              return (
                <>
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 mr-1.5 align-middle" />
                  Sending from <code className="px-1 bg-emerald-50 text-emerald-800 rounded">{sender}</code> — deliverable to any address (all employees, any tenant). Not receiving a test? Check your spam folder and verify the recipient email is spelled correctly.
                </>
              );
            })()}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function BillingQuickLink() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api.get("/billing/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, []);

  const sub = status?.company;
  const isActive = !!status?.is_active;
  const planLabel = sub?.plan ? `${sub.plan[0].toUpperCase()}${sub.plan.slice(1)} · ${sub.interval || "monthly"}` : "No plan";
  const stateLabel = sub?.subscription_status === "trialing" ? "Trial" : (isActive ? "Active" : (sub?.subscription_status || "Inactive"));

  return (
    <Card className="rounded-2xl border-slate-200" data-testid="billing-quick-link-card">
      <CardContent className="p-5 flex items-center gap-4 flex-wrap">
        <div className="h-10 w-10 rounded-full bg-sky-50 flex items-center justify-center shrink-0">
          <CreditCard className="h-5 w-5 text-sky-700" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <h3 className="font-bold font-display text-base">Billing &amp; Subscription</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            <span className="font-semibold text-slate-700">{planLabel}</span> · {stateLabel}
            {sub?.period_end && (isActive || sub?.subscription_status === "canceled_at_period_end") && (
              <> · renews/ends {new Date(sub.period_end).toLocaleDateString()}</>
            )}
          </p>
        </div>
        <Button asChild variant="outline" data-testid="billing-quick-link-btn">
          <Link to="/admin/billing">
            Manage Plan <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

